import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CommonAuthComponent from './CommonAuthComponent';
import CustomInput from '../components/common/CustomInput';
import PasswordHistoryErrorModal from '../components/common/PasswordHistoryErrorModal';
import { changePasswordWithToken, resendPasswordResetEmail } from '../redux/modules/user/actions';
import axios from 'axios';

// Debounce hook to delay password validation
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const ChangeYourPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValidity, setPasswordValidity] = useState({
    isLongEnough: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSymbol: false,
    noPersonalInfo: true,
  });
  const [mismatchError, setMismatchError] = useState('');

  /**
   * Renders a validation indicator dot with checkmark for valid states
   * @param {boolean} isValid - Whether the validation rule is met
   * @param {string} additionalClasses - Additional CSS classes for positioning
   * @returns {JSX.Element} The validation dot component
   */
  const renderValidationDot = (isValid, additionalClasses = '') => {
    return (
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${additionalClasses} ${
        isValid ? 'bg-green-600 text-white' : 'bg-gray-400'
      }`}>
        {isValid ? '✓' : ''}
      </span>
    );
  };

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const changePasswordError = useSelector((state) => state.users.changePasswordError);
  const changePasswordLoading = useSelector((state) => state.users.changePasswordLoading);

  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const isNewUser = useMemo(() => new URLSearchParams(location.search).get('newUser') || false, [location.search]);
  const email = useMemo(() => new URLSearchParams(location.search).get('email') || '', [location.search]);
  const [tokenError, setTokenError] = useState('');
  const [validating, setValidating] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [showPasswordHistoryErrorPopup, setShowPasswordHistoryErrorPopup] = useState(false);
  const [hasPasswordHistoryError, setHasPasswordHistoryError] = useState(false);
  const [prevPasswordLoading, setPrevPasswordLoading] = useState(false);
  const debouncedPassword = useDebounce(newPassword, 500);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setTokenError('Invalid or missing reset token. Please request a new password reset.');
        setValidating(false);
        return;
      }
      try {
        const api = axios.create({ baseURL: import.meta.env.VITE_APP_API_URL || 'http://localhost:62805' });
        const response = await api.get(`/auth/validate-reset-token`, { params: { token, isNewUser } });
        setUserInfo(response.data);
        setTokenError(null);
      } catch (err) {
        const msg = err.response?.data?.message || 'This reset link has expired. Please request a new password reset.';
        setTokenError(msg);
      } finally {
        setValidating(false);
      }
    };
    validate();
  }, [token, isNewUser]);

  const validatePassword = useCallback((password, userInfo) => {
    let noPersonalInfo = true;
    if (userInfo && password) {
      const lowerCasePassword = password.toLowerCase();
      const forbiddenWords = [
        userInfo.firstName?.toLowerCase(),
        userInfo.lastName?.toLowerCase(),
        userInfo.email?.split('@')[0].toLowerCase(),
      ].filter(Boolean);

      if (userInfo.birthDate) {
        const d = new Date(userInfo.birthDate);
        forbiddenWords.push(d.getFullYear().toString());
      }

      noPersonalInfo = !forbiddenWords.some(word => lowerCasePassword.includes(word));
    }

    return {
      isLongEnough: password.length >= 10,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSymbol: /[@#^?_!{}%]/.test(password),
      noPersonalInfo,
    };
  }, []);

  useEffect(() => {
    setPasswordValidity(validatePassword(debouncedPassword, userInfo));
  }, [debouncedPassword, userInfo, validatePassword]);

  // Monitor password change success and errors
  useEffect(() => {
    if (changePasswordLoading) {
      // Loading started - reset error states
      setHasPasswordHistoryError(false);
      setShowPasswordHistoryErrorPopup(false);
    } else if (prevPasswordLoading && !changePasswordLoading) {
      // Loading stopped - check for errors
      const error = changePasswordError;
      if (error) {
        // Check if error is related to password history
        const errorMessageLower = String(error).toLowerCase();
        const isPasswordHistoryErrorCheck = ['previous', 'history', 'last five', 'previously used', 'passwordhistoryerror']
          .some(keyword => errorMessageLower.includes(keyword));
        
        if (isPasswordHistoryErrorCheck) {
          setHasPasswordHistoryError(true);
          setShowPasswordHistoryErrorPopup(true);
        }
      }
    }
    setPrevPasswordLoading(changePasswordLoading);
  }, [changePasswordLoading, changePasswordError, prevPasswordLoading]);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    if (confirmPassword && value !== confirmPassword) {
      setMismatchError('Passwords do not match');
    } else {
      setMismatchError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = Object.values(passwordValidity).every(Boolean);
    if (!isValid) {
      alert('Password does not meet all requirements.');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (!token) {
      alert('Invalid or missing reset token. Please use the link from your email.');
      return;
    }

    const successPath = '/password-changed-success' + (isNewUser ? '?isNewUser=true' : '');
    await dispatch(changePasswordWithToken(token, newPassword, navigate, successPath));
  };

  /**
   * Handles the "Reset New Link" button click
   * Extracts email from query params and calls forgotPassword action
   */
  const handleResetNewLink = () => {
    if (email) {
      // Resend from expired flow and navigate to confirmation page on success
      dispatch(
        resendPasswordResetEmail(
          email,
          () => navigate(isNewUser ? '/confirmed-new-invite' : '/confirmed-new-link'),
          !!isNewUser,
          !isNewUser // mark as expired-link resend only for existing users
        )
      );
    } else {
      // If no email in query params, navigate to forgot password page
      navigate('/forgot-password');
    }
  };

  if (validating) {
    return (
      <CommonAuthComponent>
        <div className="text-center py-10">Validating reset link...</div>
      </CommonAuthComponent>
    );
  }

  if (tokenError) {
    return (
      <CommonAuthComponent>
        {/* Link Expired Modal */}
        <div className="max-w-md mx-auto">
          {/* Title */}
          <h1 className="text-[22px] text-center mb-[10px] font-medium leading-[30px]">
            {isNewUser ? 'Invitation Link Expired' : 'Link Expired'} 
          </h1>

          {/* Main Message */}
          <div className="text-center mb-4">
            <p className="text-center text-[16px] mb-[26px] font-normal leading-[22px]">
              {isNewUser ? 'This account creation link has expired or has already been used. Please request a new invitation to create your account.' 
              : 'This password reset link has expired or is no longer valid. Please request a new password reset link to continue.'}
            </p>
            <p className={`text-[12px] text-teal-300 mx-auto leading-[20px]`}>
              {isNewUser ? 'For security, invitation links expire after 14 days' : 'For security, password reset links expire after 15 minutes'}
            </p>
          </div>

          {/* Buttons */}
          <div className="mt-[20px]">
            <button
              type="button"
              className="w-full inline-flex disabled:opacity-50 rounded-[4px] border transition-all duration-300 text-body-lg items-center font-semibold justify-center px-6 py-3.5
            bg-REELTeal border border-transparent hover:bg-teal-200 text-white"
              onClick={handleResetNewLink}
            >
              {isNewUser ? 'Request New Invitation Link' : 'Resend New Link'}
            </button>
            <button
              type="button"
              className="w-full mt-4 inline-flex disabled:opacity-50 rounded-[4px] border transition-all duration-300 text-body-lg items-center font-semibold justify-center px-6 py-3.5"
              onClick={() => navigate('/login')}
            >
              Cancel
            </button>
          </div>
        </div>
      </CommonAuthComponent>
    );
  }

  return (
    <CommonAuthComponent>
      {/* Heading */}
      <h1 className="text-[22px] text-center mb-[40px] font-medium leading-[30px]">
        {isNewUser ? 'Create Your Password' : 'Change Your Password'}
      </h1>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore
        className={`mt-[40px] `}
      >
        {/* Email (read-only for new user) */}
        {isNewUser && (
          <div className="relative mb-4">
            <CustomInput
              label="Email Address"
              placeholder="Enter Email Address"
              name="email"
              type="text"
              value={userInfo?.email || ''}
              disabled
              showOptionalLabel={false}
              className='bg-white'
            />
          </div>
        )}
        {/* New Password Field */}
        <div className="relative">
          <CustomInput
            label="New Password"
            placeholder="Enter New Password"
            name="npwd"
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={handlePasswordChange}
            required
            autoComplete="off"
            hideHistory
            className='bg-white mb-4'
          />
        </div>

        {/* Confirm Password Field */}
        <div className="relative">
          <CustomInput
            label="Confirm New Password"
            placeholder="Confirm New Password"
            name="cpwd"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (newPassword && e.target.value !== newPassword) {
                setMismatchError('Passwords do not match');
              } else {
                setMismatchError('');
              }
            }}
            required
            autoComplete="off"
            hideHistory
            className={`bg-white ${newPassword ? 'mb-4' : 'mb-8'}`}
          />

          {mismatchError && <div className="text-red-600 text-[14px] mt-1 pl-1">{mismatchError}</div>}
        </div>

        {/* Password Rules - outlined box below Confirm; appears only when typing */}
        {newPassword && (
          <div className="border border-gray-300 rounded-md p-3 bg-white mt-2 !mb-8 text-sm !text-teal-300">
            <div className="font-medium text-teal-300 text-[18px] mb-2">Your password must contain:</div>
            <ul className="space-y-2">
              <li className="flex items-center gap-3 text-[14px] font-normal leading-relaxed">
                {renderValidationDot(passwordValidity.isLongEnough)}
                <span>Minimum of 10 characters</span>
              </li>
              <li className="flex items-center gap-3 text-[14px] font-normal leading-relaxed">
                {renderValidationDot(passwordValidity.hasUpperCase)}
                <span>At least one uppercase letter</span>
              </li>
              <li className="flex items-center gap-3 text-[14px] font-normal leading-relaxed">
                {renderValidationDot(passwordValidity.hasLowerCase)}
                <span>At least one lowercase letter</span>
              </li>
              <li className="flex items-center gap-3 text-[14px] font-normal leading-relaxed">
                {renderValidationDot(passwordValidity.hasNumber)}
                <span>At least one numeral (0–9)</span>
              </li>
              <li className="flex items-center gap-3 text-[14px] font-normal leading-relaxed">
                {renderValidationDot(passwordValidity.hasSymbol)}
                <span>At least one symbol (@#^?_!{ }%)</span>
              </li>
              <li className="flex items-start gap-3 text-[14px] font-normal leading-relaxed">
                {renderValidationDot(passwordValidity.noPersonalInfo, 'mt-0.5')}
                <span className="pt-0.5">Avoid using personal information such as your name, email, birthdate or address.</span>
              </li>
            </ul>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!(Object.values(passwordValidity).every(Boolean) && newPassword && confirmPassword && newPassword === confirmPassword)}
          className="w-full inline-flex disabled:opacity-50 rounded-[4px] border transition-all duration-300 text-body-lg items-center font-semibold justify-center px-6 py-3.5
            bg-REELTeal border border-transparent hover:bg-teal-200 text-white"
        >
          Set New Password
        </button>
      </form>

      {/* Password History Error Popup - shown when password history error occurs */}
      {showPasswordHistoryErrorPopup && hasPasswordHistoryError && (
        <PasswordHistoryErrorModal
          open={showPasswordHistoryErrorPopup}
          onClose={() => {
            setShowPasswordHistoryErrorPopup(false);
            setHasPasswordHistoryError(false);
            dispatch({ type: "CLEAR_CHANGE_PASSWORD_ERROR" });
          }}
          clearErrorActionType="CLEAR_CHANGE_PASSWORD_ERROR"
        />
      )}
    </CommonAuthComponent>
  );
};

export default ChangeYourPassword;